import "../css/app.css";
import "./justified-layout.js";

// Theme toggle + arrow-key photo navigation, ported from photos.brusman.se.
// Initial theme is set pre-paint by the inline script in the <head>; this wires
// the toggle button and keeps localStorage / data-theme / the icon in sync.
const THEMES = { LIGHT: "light", DARK: "dark" };
const ICONS = {
  light:
    '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block"><circle cx="12" cy="12" r="4"/><path d="M12 2v2"/><path d="M12 20v2"/><path d="m4.93 4.93 1.41 1.41"/><path d="m17.66 17.66 1.41 1.41"/><path d="M2 12h2"/><path d="M20 12h2"/><path d="m6.34 17.66-1.41 1.41"/><path d="m19.07 4.93-1.41 1.41"/></svg>',
  dark: '<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" style="display:block"><path d="M12 3a6 6 0 0 0 9 9 9 9 0 1 1-9-9Z"/><path d="M19 3v4"/><path d="M21 5h-4"/></svg>',
};

const normalize = (v) => (v === THEMES.LIGHT || v === THEMES.DARK ? v : null);
const preferred = () =>
  window.matchMedia && window.matchMedia("(prefers-color-scheme: dark)").matches ? THEMES.DARK : THEMES.LIGHT;

const setTheme = (theme) => {
  const t = theme === THEMES.DARK ? THEMES.DARK : THEMES.LIGHT;
  document.documentElement.setAttribute("data-theme", t);
  document.documentElement.classList.toggle("dark", t === THEMES.DARK);
  try {
    localStorage.setItem("theme", t);
  } catch (e) {}
  return t;
};

const updateButton = (button, icon, label, theme) => {
  const isDark = theme === THEMES.DARK;
  button.setAttribute("aria-pressed", isDark ? "true" : "false");
  if (icon) icon.innerHTML = ICONS[theme];
  if (label) label.textContent = isDark ? "Switch to light theme" : "Switch to dark theme";
};

document.addEventListener("DOMContentLoaded", () => {
  const button = document.getElementById("theme-toggle");
  if (button) {
    const icon = document.getElementById("theme-icon");
    const label = document.getElementById("theme-toggle-label");
    let current = normalize(localStorage.getItem("theme")) || preferred();
    current = setTheme(current);
    updateButton(button, icon, label, current);

    button.addEventListener("click", () => {
      current = setTheme(current === THEMES.DARK ? THEMES.LIGHT : THEMES.DARK);
      updateButton(button, icon, label, current);
    });
  }
});

// Arrow keys page between photos on the detail view.
document.addEventListener("keydown", (e) => {
  // Modified arrows are browser/OS shortcuts (Cmd+Left = Back) — leave them be.
  if (e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
  if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") return;
  const link =
    e.key === "ArrowLeft"
      ? document.getElementById("prev-link")
      : e.key === "ArrowRight"
        ? document.getElementById("next-link")
        : null;
  if (link) {
    e.preventDefault();
    window.location.href = link.href;
  }
});

// Horizontal swipe pages photos on touch screens. The held photo travels with
// the thumb and its neighbours ride along one screen out to either side, so the
// gesture shows where you're going instead of dragging the photo off into white
// page. Past the threshold the strip finishes its journey and only then swaps
// pages — the destination photo is already sitting centre screen by that point,
// so the navigation itself is invisible. Under the threshold it springs back.
// Mostly-vertical gestures are scrolling and multi-touch is pinch-zoom — both
// left alone. The photo itself stays inert to taps; only a real swipe navigates.
const SWIPE_ENGAGE = 12; // px of clear horizontal travel before the photo moves
const SWIPE_COMMIT = 60; // px at release before it counts as paging
const SWIPE_GAP = 24; // breathing room between the photos in the strip
const SWIPE_GLIDE = "transform 260ms cubic-bezier(0.22, 1, 0.36, 1)";
const SWIPE_SETTLE = 280; // just past the glide, so it has landed before we leave

let touchX = null;
let touchY = null;
let held = null; // the photo following the thumb, once a swipe is unambiguous
let strip = []; // its neighbours, travelling with it
let onPhoto = false; // cached per gesture: elsewhere every move handler bails

const horizontal = (dx, dy) => Math.abs(dx) >= Math.abs(dy) * 1.5;
const stripSpan = () => window.innerWidth + SWIPE_GAP;

// A swipe animates the next photo into place itself, so the paging transition on
// the far side would move it a second time. This flag tells it to stand down.
const SWIPE_NAV = "swipe-nav";
const flagSwipeNav = () => {
  try {
    sessionStorage.setItem(SWIPE_NAV, "1");
  } catch (e) {}
};
const takeSwipeNav = () => {
  try {
    if (!sessionStorage.getItem(SWIPE_NAV)) return false;
    sessionStorage.removeItem(SWIPE_NAV);
    return true;
  } catch (e) {
    return false;
  }
};

// The gutter links carry each neighbour's srcset/sizes and its dimensions — and
// carry them only when there really is a neighbour, since at a stream's end
// they're exit links instead. warm() has already pulled the image into the cache,
// so it paints at once.
//
// The box takes the neighbour's OWN aspect ratio, so it arrives at the size it
// will actually be on its own page: a portrait coming in beside a landscape is
// taller than the photo it's replacing, not squashed into its shape.
const buildNeighbour = (side) => {
  const link = document.getElementById(`${side}-link`);
  if (!link?.dataset.srcset) return null;
  const box = document.createElement("div");
  box.className = "swipe-neighbour";
  box.dataset.side = side;
  const { w, h } = link.dataset;
  if (w && h) box.style.aspectRatio = `${w} / ${h}`;
  const img = document.createElement("img");
  img.sizes = link.dataset.sizes || "100vw";
  img.srcset = link.dataset.srcset;
  img.alt = ""; // decorative: it's the next page's photo, announced when we land
  box.setAttribute("aria-hidden", "true");
  box.append(img);
  return box;
};

const dropNeighbours = () => {
  for (const n of document.querySelectorAll(".swipe-neighbour")) n.remove();
  liftStage(false);
};

// Move the whole strip as one: the held photo by dx, each neighbour from its
// resting place a screen out to one side.
const slideStrip = (photo, neighbours, dx) => {
  photo.style.transform = `translateX(${dx}px)`;
  for (const n of neighbours) {
    const rest = n.dataset.side === "next" ? stripSpan() : -stripSpan();
    n.style.transform = `translateX(${rest + dx}px)`;
  }
};

// A neighbour taller than the photo it's arriving beside reaches down over the
// meta column, which shares the stage's z-10 and would otherwise paint across it.
const liftStage = (on) => {
  const stage = document.querySelector(".vt-photo-stage");
  if (stage) stage.style.zIndex = on ? "20" : "";
};

const glideStrip = (photo, neighbours, dx, done) => {
  photo.style.transition = SWIPE_GLIDE;
  for (const n of neighbours) n.style.transition = SWIPE_GLIDE;
  slideStrip(photo, neighbours, dx);
  setTimeout(done, SWIPE_SETTLE);
};

const restPhoto = (photo) => {
  photo.style.transition = "";
  photo.style.transform = "";
};

document.addEventListener(
  "touchstart",
  (e) => {
    if (e.touches.length !== 1) {
      touchX = touchY = null;
      return;
    }
    touchX = e.touches[0].clientX;
    touchY = e.touches[0].clientY;
    onPhoto = !!mainPhoto(); // .vt-photo only exists on the detail page
  },
  { passive: true },
);

// Not passive: once the drag is under way it has to claim the gesture, or a
// less-than-perfectly-horizontal swipe scrolls the page at the same time.
document.addEventListener(
  "touchmove",
  (e) => {
    if (!onPhoto || touchX === null || e.touches.length !== 1) return;
    const dx = e.touches[0].clientX - touchX;
    const dy = e.touches[0].clientY - touchY;
    if (!held) {
      // Hold off until the gesture is unambiguously sideways, so a scroll never
      // twitches the photo. Reduced motion keeps the old instant navigation.
      if (Math.abs(dx) < SWIPE_ENGAGE || !horizontal(dx, dy)) return;
      if (!matchMedia("(prefers-reduced-motion: no-preference)").matches) return;
      held = mainPhoto();
      if (!held) return;
      held.style.transition = "none";
      dropNeighbours(); // leftovers from a gesture that never finished
      strip = ["prev", "next"].map(buildNeighbour).filter(Boolean);
      for (const n of strip) {
        n.style.transition = "none";
        held.parentElement.append(n);
      }
      liftStage(true);
    }
    // Claim the gesture so the page doesn't scroll along with the drag. If the
    // browser already started scrolling (a swipe that began diagonally) the event
    // isn't cancelable any more and this is its call to make, not ours.
    if (e.cancelable) e.preventDefault();
    slideStrip(held, strip, dx);
  },
  { passive: false },
);

document.addEventListener(
  "touchend",
  (e) => {
    if (touchX === null) return;
    const dx = e.changedTouches[0].clientX - touchX;
    const dy = e.changedTouches[0].clientY - touchY;
    const photo = held;
    const neighbours = strip;
    touchX = touchY = held = null;
    strip = [];

    const paging = Math.abs(dx) >= SWIPE_COMMIT && horizontal(dx, dy);
    // At a stream's end these ids carry the exit link instead, so a fling there
    // leaves the photo the same way the arrows would.
    const link = paging ? document.getElementById(dx < 0 ? "next-link-mobile" : "prev-link-mobile") : null;
    if (!photo) {
      // Never engaged — reduced motion, or a flick too quick to track.
      if (link) window.location.href = link.href;
      return;
    }
    // Nothing to slide towards (the exit link): let the photo settle and leave
    // the way tapping the arrow would.
    const heading = dx < 0 ? "next" : "prev";
    if (link && !neighbours.some((n) => n.dataset.side === heading)) {
      dropNeighbours();
      restPhoto(photo);
      window.location.href = link.href;
      return;
    }
    if (!link) {
      return glideStrip(photo, neighbours, 0, () => {
        dropNeighbours();
        restPhoto(photo);
      });
    }
    glideStrip(photo, neighbours, dx < 0 ? -stripSpan() : stripSpan(), () => {
      flagSwipeNav();
      window.location.href = link.href;
    });
  },
  { passive: true },
);

document.addEventListener(
  "touchcancel",
  () => {
    const photo = held;
    const neighbours = strip;
    touchX = touchY = held = null;
    strip = [];
    if (photo) {
      glideStrip(photo, neighbours, 0, () => {
        dropNeighbours();
        restPhoto(photo);
      });
    }
  },
  { passive: true },
);

// Inline transforms survive in the bfcache — without this, a photo you swiped
// away comes back still shoved aside when you hit Back.
window.addEventListener("pageshow", () => {
  dropNeighbours();
  const photo = mainPhoto();
  if (photo) restPhoto(photo);
  takeSwipeNav(); // a swipe on a browser without cross-document transitions
});

// Warm the cache for a photo the visitor is about to open: an off-DOM <img>
// with the detail page's srcset/sizes makes the browser pick (and cache) the
// exact candidate that page will render. Full-size photos are real data, so
// nothing is prefetched on metered or slow connections.
const warm = (srcset, sizes) => {
  const conn = navigator.connection;
  if (!srcset || (conn && (conn.saveData || /(^|-)[23]g$/.test(conn.effectiveType || "")))) return;
  const img = new Image();
  img.sizes = sizes || "100vw";
  img.srcset = srcset;
};

// The adjacent photos, so flipping feels instant (the gutter links carry the
// neighbour's srcset/sizes from the server).
window.addEventListener("load", () => {
  for (const id of ["prev-link", "next-link"]) {
    const link = document.getElementById(id);
    warm(link?.dataset.srcset, link?.dataset.sizes);
  }
});

// A hovered (or touched) grid tile, so the tile→photo morph lands on a cached
// image instead of racing the network. The tile already carries the full
// srcset; only the detail page's sizes differ, recomputed here with the same
// height-capped formula the photo page uses — the 8rem below is the photo cap
// and must track the one in photos/show.rb. One warm per URL per page.
const warmedTiles = new Set();
const warmTile = (target) => {
  const a = target.closest?.("[data-image-grid] a[href]");
  const img = a?.querySelector("img[srcset]");
  if (!img || warmedTiles.has(a.href)) return;
  warmedTiles.add(a.href);
  const w = img.getAttribute("width");
  const h = img.getAttribute("height");
  warm(
    img.getAttribute("srcset"),
    w && h && `(min-aspect-ratio: ${w}/${h}) calc((100vh - 8rem) * ${w} / ${h}), 100vw`,
  );
};
document.addEventListener("pointerover", (e) => warmTile(e.target));
document.addEventListener("touchstart", (e) => warmTile(e.target), { passive: true });

// Cross-document view transitions: the CSS opts every navigation in
// (tailwind.css); these handlers only steer the photo pairing. Three cases:
//   • prev/next paging — tag the transition photo-fwd/photo-back so the CSS
//     slides the stage, and drop the photo's own name so the two different
//     photographs don't also morph into each other mid-slide.
//   • grid tile → photo (and back) — give the tile the `photo` name for the
//     duration, so the thumbnail and the full image morph into one another.
//   • anything else — the default root crossfade, no bookkeeping.
// Browsers without cross-document transitions never fire these with a
// viewTransition, so every handler bails immediately there.
const PHOTO_PATH = /\/photo\/([A-Za-z0-9]+)\/?$/;
const sqidOf = (url) => {
  try {
    const u = new URL(url, location.href);
    return u.origin === location.origin ? (u.pathname.match(PHOTO_PATH) || [])[1] : undefined;
  } catch (e) {
    return undefined;
  }
};
const navHref = (id) => document.getElementById(id)?.href;
const mainPhoto = () => document.querySelector(".vt-photo");
const tileFor = (sqid) =>
  sqid ? document.querySelector(`[data-image-grid] a[href$="/photo/${sqid}"] img`) : null;

// Inline names linger on bfcache-restored pages; stale duplicates would make
// the browser skip the whole transition, so each handler starts clean.
const clearInlineNames = () => {
  for (const el of document.querySelectorAll('[style*="view-transition-name"]')) {
    el.style.viewTransitionName = "";
  }
};

// finished rejects when a transition is skipped; restore either way, quietly.
const restoreAfter = (vt, restore) => vt.finished.then(restore, restore);

window.addEventListener("pageswap", (e) => {
  if (!e.viewTransition || !e.activation) return;
  clearInlineNames();
  const to = e.activation.entry?.url;
  const toSqid = sqidOf(to);
  if (!toSqid) return; // leaving photos entirely — defaults handle it
  if (to === navHref("prev-link") || to === navHref("next-link")) {
    mainPhoto()?.style.setProperty("view-transition-name", "none");
    return;
  }
  const tile = tileFor(toSqid);
  if (tile) {
    mainPhoto()?.style.setProperty("view-transition-name", "none");
    tile.style.viewTransitionName = "photo";
  }
});

window.addEventListener("pagereveal", (e) => {
  if (!e.viewTransition) return;
  // A swipe already glided this photo to centre screen before leaving, so the
  // page underneath it is simply the same picture — transitioning would move it
  // a second time.
  if (takeSwipeNav()) {
    e.viewTransition.skipTransition();
    return;
  }
  clearInlineNames();
  const from = window.navigation?.activation?.from?.url || document.referrer;
  if (!from) return;
  const img = mainPhoto();
  if (img) {
    // On a photo page: the old page being my prev means we moved forward.
    // At a stream's end the prev/next ids are reused by the exit links, so
    // only a from-URL that is itself a photo page counts as paging.
    if (!sqidOf(from)) return;
    if (from === navHref("prev-link")) e.viewTransition.types.add("photo-fwd");
    else if (from === navHref("next-link")) e.viewTransition.types.add("photo-back");
    else return; // arrived from a grid or elsewhere — let the morph pair up
    img.style.viewTransitionName = "none";
    restoreAfter(e.viewTransition, () => (img.style.viewTransitionName = ""));
    return;
  }
  // On a grid page, arriving back from a photo: morph it into its tile.
  const tile = tileFor(sqidOf(from));
  if (tile) {
    tile.style.viewTransitionName = "photo";
    restoreAfter(e.viewTransition, () => (tile.style.viewTransitionName = ""));
  }
});

// Photo-page heart: anonymous, one-way. Server-side it's a plain form POST
// (works without JS via a redirect); this upgrades it to fetch + localStorage,
// which remembers this browser already liked so the fill survives revisits.
// The server dedups per visitor+day regardless, so repeats are harmless. The
// count updates optimistically and settles on the server's answer.
document.addEventListener("DOMContentLoaded", () => {
  const btn = document.getElementById("like-button");
  if (!btn) return;
  const count = document.getElementById("like-count");
  const key = "liked:" + btn.dataset.sqid;
  const remembered = () => {
    try {
      return localStorage.getItem(key);
    } catch (e) {
      return null;
    }
  };
  const mark = () => {
    btn.classList.add("liked");
    btn.setAttribute("aria-pressed", "true");
    btn.setAttribute("aria-label", "Liked");
  };
  if (remembered()) mark();

  // The once-ever spark scatter, centered on the heart glyph. Click only —
  // a revisit's restored heart doesn't re-celebrate.
  function burst() {
    const b = document.createElement("span");
    b.className = "like-burst";
    for (let i = 0; i < 6; i++) {
      const s = document.createElement("i");
      s.style.setProperty("--a", i * 60 + 15 + "deg");
      b.appendChild(s);
    }
    btn.appendChild(b);
    setTimeout(() => b.remove(), 800);
  }

  btn.form?.addEventListener("submit", async (e) => {
    e.preventDefault(); // JS path: fetch instead of the full-page form POST
    if (btn.classList.contains("liked")) return;
    mark();
    btn.classList.add("just-liked");
    burst();
    if (count) {
      count.textContent = String((parseInt(count.textContent, 10) || 0) + 1);
      count.classList.add("bumped");
    }
    try {
      localStorage.setItem(key, "1");
    } catch (e) {}
    try {
      const res = await fetch(btn.form.action, { method: "POST", headers: { Accept: "application/json" } });
      if (res.ok && count) count.textContent = String((await res.json()).count);
    } catch (e) {
      // Optimistic state stands; the like lands on a retry from another view.
    }
  });
});
